export interface IpLocationResult {
  city?: string;
  region?: string;
  country?: string;
  source: 'ipapi' | 'ipwhois';
  timestamp: number;
}

const STORAGE_KEY = 'icope_ip_location_v1';

// 常见城市英中翻译映射表
const CITY_NAME_MAP: Record<string, string> = {
  // 中国城市（拼音/英文 -> 中文）
  'Chengdu': '成都',
  'Beijing': '北京',
  'Shanghai': '上海',
  'Guangzhou': '广州',
  'Shenzhen': '深圳',
  'Hangzhou': '杭州',
  'Nanjing': '南京',
  'Chongqing': '重庆',
  'Wuhan': '武汉',
  'Xian': '西安',
  "Xi'an": '西安',
  'Suzhou': '苏州',
  'Tianjin': '天津',
  'Zhengzhou': '郑州',
  'Changsha': '长沙',
  'Qingdao': '青岛',
  'Dalian': '大连',
  'Dongguan': '东莞',
  'Foshan': '佛山',
  'Ningbo': '宁波',
  'Xiamen': '厦门',
  'Kunming': '昆明',
  'Hefei': '合肥',
  'Fuzhou': '福州',
  'Jinan': '济南',
  'Shenyang': '沈阳',
  'Harbin': '哈尔滨',
  'Changchun': '长春',
  'Nanchang': '南昌',
  'Guiyang': '贵阳',
  'Nanning': '南宁',
  'Lanzhou': '兰州',
  'Taiyuan': '太原',
  'Shijiazhuang': '石家庄',
  'Urumqi': '乌鲁木齐',
  'Hohhot': '呼和浩特',
  'Lhasa': '拉萨',
  'Haikou': '海口',
  'Yinchuan': '银川',
  'Xining': '西宁',
  // 海外常见城市
  'Los Angeles': '洛杉矶',
  'New York': '纽约',
  'San Francisco': '旧金山',
  'Seattle': '西雅图',
  'Chicago': '芝加哥',
  'Boston': '波士顿',
  'Washington': '华盛顿',
  'Houston': '休斯顿',
  'San Diego': '圣地亚哥',
  'San Jose': '圣何塞',
  'London': '伦敦',
  'Paris': '巴黎',
  'Tokyo': '东京',
  'Seoul': '首尔',
  'Singapore': '新加坡',
  'Sydney': '悉尼',
  'Melbourne': '墨尔本',
  'Toronto': '多伦多',
  'Vancouver': '温哥华',
  'Hong Kong': '香港',
  'Macau': '澳门',
  'Taipei': '台北',
};

let cachedLocation: IpLocationResult | null = null;
let inFlight: Promise<IpLocationResult | null> | null = null;

const normalizeCityName = (name?: string): string | undefined => {
  if (!name) return undefined;
  // 1. 移除"市"后缀并去除空格
  let normalized = name.replace(/市$/, '').trim();
  // 2. 尝试翻译英文城市名为中文
  if (CITY_NAME_MAP[normalized]) {
    normalized = CITY_NAME_MAP[normalized];
  }
  return normalized;
};

const readCache = (): IpLocationResult | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IpLocationResult;
    if (parsed?.city) {
      // 对缓存的城市名也进行翻译（修复英文城市名未翻译的问题）
      const translatedCity = normalizeCityName(parsed.city);
      return {
        ...parsed,
        city: translatedCity
      };
    }
  } catch (error) {
    console.warn('[locationService] Failed to parse cached location', error);
  }
  return null;
};

const writeCache = (location: IpLocationResult) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(location));
  } catch (error) {
    console.warn('[locationService] Failed to cache location', error);
  }
};

const fetchJson = async (url: string) => {
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return response.json();
};

const fetchFromIpApi = async (): Promise<IpLocationResult | null> => {
  try {
    const data = await fetchJson('https://ipapi.co/json/');
    if (!data || data.error) {
      return null;
    }
    const city = normalizeCityName(data.city || data.region);
    if (!city) {
      return null;
    }
    return {
      city,
      region: data.region,
      country: data.country_name,
      source: 'ipapi',
      timestamp: Date.now(),
    };
  } catch (error) {
    console.warn('[locationService] ipapi request failed', error);
    return null;
  }
};

const fetchFromIpWhois = async (): Promise<IpLocationResult | null> => {
  try {
    const data = await fetchJson('https://ipwho.is/');
    if (!data || data.success === false) {
      return null;
    }
    const city = normalizeCityName(data.city || data.region);
    if (!city) {
      return null;
    }
    return {
      city,
      region: data.region,
      country: data.country,
      source: 'ipwhois',
      timestamp: Date.now(),
    };
  } catch (error) {
    console.warn('[locationService] ipwhois request failed', error);
    return null;
  }
};

const resolveLocation = async (): Promise<IpLocationResult | null> => {
  const primary = await fetchFromIpApi();
  if (primary) {
    return primary;
  }
  return fetchFromIpWhois();
};

export const locationService = {
  async getApproximateCity(forceRefresh = false): Promise<IpLocationResult | null> {
    if (cachedLocation && !forceRefresh) {
      return cachedLocation;
    }

    const cached = forceRefresh ? null : readCache();
    if (cached) {
      cachedLocation = cached;
      return cached;
    }

    if (inFlight) {
      return inFlight;
    }

    inFlight = resolveLocation()
      .then((location) => {
        if (location) {
          cachedLocation = location;
          writeCache(location);
        }
        return location;
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  },
};
